(() => {
  'use strict';

  const ROOT_SELECTOR = '[data-mmg-customer-intelligence="v1"]';
  const SIGNAL_EVENT = 'mmg:customer-intelligence:signal';
  const SHADOW_CONTEXT_EVENT = 'mmg:kairos-session:shadow-context';
  const FLUSH_INTERVAL_MS = 15_000;
  const MAX_SIGNALS_PER_BATCH = 12;
  const MAX_PRESENTED_IDS = 40;
  const ENDPOINT_PATH = '/api/kairos/session-intelligence/signals';
  const ALLOWED_SIGNAL_TYPES = new Set([
    'PRODUCT_VIEW',
    'JOURNAL_VIEW',
    'GOVERNED_PAGE_VIEW',
    'SERVICE_VIEW',
    'RESOURCE_FINDER_SELECTION',
    'EXPLICIT_PROBLEM_SELECTION',
    'SITE_SEARCH',
    'RECOMMENDATION_CLICK',
    'EXPLICIT_KAIROS_STATEMENT',
  ]);
  const ALLOWED_SIGNAL_KEYS = new Set([
    'type',
    'resourceId',
    'normalizedIntent',
    'problemDomain',
    'strategicPillar',
    'journeyStage',
  ]);

  let resolveInitialContextReady;
  let initialContextSettled = false;
  const initialContextReady = new Promise((resolve) => {
    resolveInitialContextReady = resolve;
  });
  window.MMGKairosSessionInitialContextReady = initialContextReady;

  function settleInitialContext() {
    if (initialContextSettled) return;
    initialContextSettled = true;
    resolveInitialContextReady();
  }

  function cleanString(value, max = 220) {
    if (typeof value !== 'string') return null;
    const cleaned = value.trim();
    return cleaned && cleaned.length <= max ? cleaned : null;
  }

  function cleanEligibleResourceIDs(value) {
    if (!Array.isArray(value)) return [];
    return [...new Set(value
      .filter((item) => typeof item === 'string' && item.trim().length)
      .map((item) => item.trim()))];
  }

  function validateEndpoint(value) {
    const raw = cleanString(value, 500);
    if (!raw) return null;
    try {
      const url = new URL(raw, window.location.origin);
      const hostAllowed = url.hostname === 'themindsetmediagroup.com'
        || url.hostname.endsWith('.themindsetmediagroup.com');
      if (url.protocol !== 'https:' || !hostAllowed || url.pathname !== ENDPOINT_PATH) return null;
      url.search = '';
      url.hash = '';
      return url.toString();
    } catch (_) {
      return null;
    }
  }

  function sanitizeSignal(input) {
    if (!input || typeof input !== 'object' || Array.isArray(input)) return null;
    const signal = {};
    for (const key of ALLOWED_SIGNAL_KEYS) {
      if (!(key in input)) continue;
      if (key === 'type') signal.type = cleanString(input.type, 80);
      else signal[key] = cleanString(input[key], 220);
    }
    if (!ALLOWED_SIGNAL_TYPES.has(signal.type)) return null;
    for (const key of Object.keys(signal)) {
      if (signal[key] == null) delete signal[key];
    }
    return signal;
  }

  function sanitizeShadowContext(body) {
    if (!body || typeof body !== 'object' || Array.isArray(body)) return null;
    if ('sessionCorrelationId' in body) return null;
    if (body.ok !== true || body.mode !== 'SHADOW' || body.customerVisible !== false) return null;
    return Object.freeze({
      mode: 'SHADOW',
      customerVisible: false,
      serverValidated: true,
      sessionActive: body.sessionActive === true,
      conductorReady: body.conductorReady === true,
      fallbackRequired: body.fallbackRequired !== false,
      reason: cleanString(body.reason, 160),
      graphVersion: cleanString(body.graphVersion, 220),
      provenanceVersion: cleanString(body.provenanceVersion, 220),
      eligibleResourceIDs: cleanEligibleResourceIDs(body.eligibleResourceIds),
    });
  }

  function publishShadowContext(body) {
    const context = sanitizeShadowContext(body);
    if (!context) return null;
    window.MMGKairosSessionShadowContext = context;
    window.dispatchEvent(new CustomEvent(SHADOW_CONTEXT_EVENT, { detail: context }));
    return context;
  }

  function pageViewSignal(root) {
    const resourceType = cleanString(root.dataset.mmgResourceType, 120);
    const type = resourceType === 'PROFESSIONAL_SERVICE'
      ? 'SERVICE_VIEW'
      : resourceType === 'MINDSET_JOURNAL_ARTICLE'
        ? 'JOURNAL_VIEW'
        : resourceType === 'SHOPIFY_PRODUCT' || resourceType === 'DIGITAL_ASSET'
          ? 'PRODUCT_VIEW'
          : 'GOVERNED_PAGE_VIEW';
    return sanitizeSignal({
      type,
      resourceId: root.dataset.mmgResourceId,
      problemDomain: root.dataset.mmgProblemDomain,
      strategicPillar: String(root.dataset.mmgStrategicPillars || '').split(',')[0],
      journeyStage: root.dataset.mmgJourneyStage,
    });
  }

  function batchId() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID();
    }
    return `b-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
  }

  function boot() {
    const root = document.querySelector(ROOT_SELECTOR);
    if (!root || root.dataset.mmgKairosSessionBridgeEnabled !== 'true') {
      settleInitialContext();
      return;
    }
    const endpoint = validateEndpoint(root.dataset.mmgKairosSessionEndpoint);
    if (!endpoint) {
      settleInitialContext();
      return;
    }

    let queue = [];
    let timer = null;
    let inFlight = false;

    const schedule = () => {
      if (timer || queue.length === 0) return;
      timer = window.setTimeout(() => {
        timer = null;
        void flush(false);
      }, FLUSH_INTERVAL_MS);
    };

    const enqueue = (candidate) => {
      const signal = sanitizeSignal(candidate);
      if (!signal || queue.length >= MAX_SIGNALS_PER_BATCH) return false;
      queue.push(signal);
      schedule();
      return true;
    };

    async function flush(keepalive) {
      if (inFlight || queue.length === 0) return;
      const signals = queue.slice(0, MAX_SIGNALS_PER_BATCH);
      queue = queue.slice(signals.length);
      inFlight = true;
      try {
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          credentials: 'include',
          keepalive: keepalive === true,
          body: JSON.stringify({
            batchId: batchId(),
            occurredAt: new Date().toISOString(),
            signals,
          }),
        });
        if (response.ok) {
          const body = await response.json().catch(() => null);
          publishShadowContext(body);
        }
      } catch (_) {
        // Session intelligence is optional progressive enhancement. Do not block the storefront.
      } finally {
        inFlight = false;
        if (queue.length) schedule();
      }
    }

    document.addEventListener(SIGNAL_EVENT, (event) => {
      enqueue(event?.detail ?? null);
    });

    const initial = pageViewSignal(root);
    if (initial && enqueue(initial)) {
      if (timer) {
        window.clearTimeout(timer);
        timer = null;
      }
      Promise.resolve(flush(false)).finally(settleInitialContext);
    } else {
      settleInitialContext();
    }

    window.addEventListener('pagehide', () => {
      if (timer) {
        window.clearTimeout(timer);
        timer = null;
      }
      void flush(true);
    }, { once: true });

    window.MMGKairosSessionSignals = Object.freeze({
      version: 'v1',
      queueSignal: (signal) => enqueue(signal),
      flush: () => flush(false),
      maxSignalsPerBatch: MAX_SIGNALS_PER_BATCH,
      maxPresentedIds: MAX_PRESENTED_IDS,
    });
  }

  function scheduleBoot() {
    const start = () => {
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(() => boot(), { timeout: 2500 });
      } else {
        window.setTimeout(boot, 750);
      }
    };
    if (document.readyState === 'complete') start();
    else window.addEventListener('load', start, { once: true });
  }

  scheduleBoot();
})();
